import {useEffect, useState} from "react";
import {ArrowUpRight, MessageCircle, Star, HelpCircle, Users as UsersIcon, Download, ShieldAlert, LogOut, MapPin, Link2, Pencil} from "lucide-react";
import {apiGet, apiPut, API} from "@/lib/api";
import axios from "axios";
import {TRADITIONS} from "@/lib/constants";
import EditProfileModal from "@/pages/modals/EditProfile";

const typeIcon = {
  message: <MessageCircle size={14}/>,
  question: <HelpCircle size={14}/>,
  answer: <MessageCircle size={14}/>,
  review: <Star size={14}/>,
};

export default function Profile({setModal, notify, user, setUser, setActive}) {
  const [activity, setActivity] = useState([]);
  const [tab, setTab] = useState("all");
  const [editing, setEditing] = useState(false);
  const [counts, setCounts] = useState({followers: 0, following: 0});

  useEffect(() => {
    if (!user) return;
    apiGet("/users/me/activity").then(setActivity).catch(() => setActivity([]));
    apiGet(`/members/${user.user_id}`).then(m => setCounts({followers: m.followers || 0, following: m.following || 0})).catch(() => {});
  }, [user]);

  const filtered = tab === "all" ? activity : activity.filter(a => a.type === tab);
  const initials = user?.name ? user.name.split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase() : "G";

  return (
    <div data-testid="profile-page">
      <div className="profile-cover">
        <div className="profile-avatar" data-testid="profile-avatar">{user?.picture ? <img src={user.picture} alt={user.name}/> : initials}</div>
      </div>
      <div className="profile-head">
        <div>
          <div className="eyebrow">{user ? `MEMBER SINCE ${new Date(user.created_at || Date.now()).getFullYear()}` : "GUEST"}</div>
          <h1>{user?.name || "Sign in to view profile"}</h1>
          {user?.headline && <p data-testid="profile-headline">{user.headline}</p>}
          {user?.city && <p className="profile-city"><MapPin size={13}/> {user.city}</p>}
        </div>
        <button data-testid="profile-edit" className="outline-button" onClick={() => setEditing(true)}><Pencil size={14}/> Edit profile</button>
      </div>
      {(user?.bio || user?.links?.length > 0) && (
        <div className="profile-about" data-testid="profile-about">
          {user.bio && <><div className="eyebrow">ABOUT ME</div><p>{user.bio}</p></>}
          {user.links?.length > 0 && <div className="profile-links">{user.links.map(l => <a key={l} href={l} target="_blank" rel="noreferrer" data-testid="profile-link"><Link2 size={12}/> {l.replace(/^https?:\/\//, "")}</a>)}</div>}
        </div>
      )}
      <div className="profile-stats">
        <span data-testid="profile-following"><b>{counts.following}</b> Following</span>
        <span data-testid="profile-followers"><b>{counts.followers}</b> Followers</span>
        <span><b>{activity.length}</b> Activity items</span>
      </div>

      <div className="profile-body">
        <section>
          <div className="activity-tabs" data-testid="activity-tabs">
            {[["all","All"],["question","Questions"],["answer","Answers"],["message","Posts"],["review","Reviews"]].map(([k,label]) => (
              <button key={k} data-testid={`activity-tab-${k}`} className={tab === k ? "activity-tab active" : "activity-tab"} onClick={() => setTab(k)}>
                {label} <small>{k === "all" ? activity.length : activity.filter(a => a.type === k).length}</small>
              </button>
            ))}
          </div>

          {!user ? (
            <div className="empty-pane">
              <UsersIcon size={25}/>
              <h3>Sign in to see your activity</h3>
              <p>Everything you post in samaj, questions, answers and reviews will show up here.</p>
              <button data-testid="profile-signin" className="coral-button" onClick={() => setModal("auth")}>Sign in</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-pane">
              <MessageCircle size={25}/>
              <h3>Nothing here yet</h3>
              <p>Ask questions, join conversations and write reviews — your activity will appear here.</p>
            </div>
          ) : (
            <div className="activity-feed">
              {filtered.map((a, i) => (
                <div key={i} className="activity-item" data-testid={`activity-item-${i}`}>
                  <span className="activity-badge">{typeIcon[a.type] || <MessageCircle size={14}/>} {a.type}</span>
                  <h4>{a.title}</h4>
                  <small>{a.context} · {new Date(a.when).toLocaleDateString()}</small>
                </div>
              ))}
            </div>
          )}
        </section>
        <aside className="profile-side">
          <div className="eyebrow">YOUR PERSONAL CONCIERGE</div>
          <h3>Anything from India.</h3>
          <p>Wedding shopping, monthly masalas or a one-off find — our India desk handles it and calls you back with discounted pricing.</p>
          <button data-testid="profile-gfi" className="coral-button" onClick={() => setActive("gfi")}>Open Your Personal Concierge <ArrowUpRight size={16}/></button>

          {user && <Preferences user={user} setUser={setUser} notify={notify}/>}
          {user && <GDPRCenter notify={notify}/>}
        </aside>
        {editing && <EditProfileModal user={user} setUser={setUser} close={() => setEditing(false)} notify={notify}/>}
      </div>
    </div>
  );
}

function Preferences({user, setUser, notify}) {
  const save = async (patch) => {
    try { const u = await apiPut("/users/me/preferences", patch); setUser(u); notify("Preferences saved"); }
    catch { notify("Please try again"); }
  };
  return (
    <div className="gdpr-center" data-testid="festival-preferences">
      <div className="eyebrow">FESTIVAL ALERTS</div>
      <h3>Never miss a tithi.</h3>
      <p>We nudge you on festival mornings based on your tradition.</p>
      <label className="pref-row">Tradition
        <select data-testid="pref-tradition" value={user.tradition || "Vaishnava"} onChange={e => save({tradition: e.target.value})}>
          {TRADITIONS.map(t => <option key={t}>{t}</option>)}
        </select>
      </label>
      <label className="pref-row check">
        <input type="checkbox" data-testid="pref-festival-emails" checked={!!user.festival_emails} onChange={e => save({festival_emails: e.target.checked})}/>
        Email me on festival mornings
      </label>
    </div>
  );
}

function GDPRCenter({notify}) {
  const [confirming, setConfirming] = useState(false);

  const exportData = async () => {
    try {
      const res = await axios.get(`${API}/users/me/export`, {withCredentials: true, responseType: "blob"});
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "indian-ocean-data.json"; a.click();
      URL.revokeObjectURL(url);
      notify("Your data download has started");
    } catch { notify("Could not export — please try again"); }
  };

  const deleteAccount = async () => {
    try {
      await axios.delete(`${API}/users/me`, {withCredentials: true});
      notify("Your account has been deleted");
      setTimeout(() => window.location.reload(), 1200);
    } catch { notify("Could not delete — please try again"); }
  };

  return (
    <div className="gdpr-center" data-testid="gdpr-center">
      <div className="eyebrow">PRIVACY &amp; DATA</div>
      <h3>Your data, your call.</h3>
      <p>Export everything you&rsquo;ve ever shared or delete your account in one tap. GDPR compliant.</p>
      <button data-testid="gdpr-export" className="outline-button full" onClick={exportData}>
        <Download size={14}/> Download my data (JSON)
      </button>
      {!confirming ? (
        <button data-testid="gdpr-delete" className="danger-link" onClick={() => setConfirming(true)}>
          <ShieldAlert size={14}/> Delete my account
        </button>
      ) : (
        <div className="gdpr-confirm">
          <p>This permanently deletes your profile, requests, saved items and private messages. Public posts are anonymised. This cannot be undone.</p>
          <div className="gdpr-confirm-actions">
            <button data-testid="gdpr-cancel" className="outline-button sm" onClick={() => setConfirming(false)}>Cancel</button>
            <button data-testid="gdpr-confirm" className="ban-btn" onClick={deleteAccount}><LogOut size={13}/> Delete forever</button>
          </div>
        </div>
      )}
    </div>
  );
}
